import React, { useState, useEffect } from 'react';
import { X, GitCompare, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { roleService } from '../../../services/roleService';

export default function RoleComparisonModal({ onClose, allRoles }) {
  const [selectedRole1, setSelectedRole1] = useState('');
  const [selectedRole2, setSelectedRole2] = useState('');
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCompare = async () => {
    if (!selectedRole1 || !selectedRole2) {
      setError('Please select two roles to compare');
      return;
    }

    if (selectedRole1 === selectedRole2) {
      setError('Please select different roles to compare');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const result = await roleService?.compareRoles(selectedRole1, selectedRole2);
      setComparison(result);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const getAccessLevelColor = (level) => {
    const colors = {
      none: 'bg-gray-100 text-gray-800',
      read: 'bg-blue-100 text-blue-800',
      write: 'bg-green-100 text-green-800',
      delete: 'bg-yellow-100 text-yellow-800',
      admin: 'bg-red-100 text-red-800'
    };
    return colors?.[level] || colors?.none;
  };

  const getDifferenceIcon = (isDifferent, role1HasMore, role2HasMore) => {
    if (!isDifferent) return <Minus className="w-4 h-4 text-gray-400" />;
    if (role1HasMore) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (role2HasMore) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <GitCompare className="w-6 h-6 text-indigo-600" />
            <h2 className="text-xl font-semibold text-gray-900">Compare Roles</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Role Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Role
              </label>
              <select
                value={selectedRole1}
                onChange={(e) => setSelectedRole1(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select a role...</option>
                {allRoles?.map((role) => (
                  <option key={role?.id} value={role?.id}>
                    {role?.displayName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Second Role
              </label>
              <select
                value={selectedRole2}
                onChange={(e) => setSelectedRole2(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select a role...</option>
                {allRoles?.map((role) => (
                  <option key={role?.id} value={role?.id}>
                    {role?.displayName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={handleCompare}
            disabled={loading || !selectedRole1 || !selectedRole2}
            className="w-full mb-6 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Comparing...
              </>
            ) : (
              <>
                <GitCompare className="w-4 h-4 mr-2" />
                Compare Roles
              </>
            )}
          </button>

          {/* Comparison Results */}
          {comparison && (
            <div>
              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">
                      {comparison?.role1?.name}
                    </h3>
                    <p className="text-xs text-gray-500">{comparison?.role1?.description}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">
                      {comparison?.role2?.name}
                    </h3>
                    <p className="text-xs text-gray-500">{comparison?.role2?.description}</p>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-700">
                    {comparison?.differenceCount} permission{comparison?.differenceCount !== 1 ? 's' : ''} differ between these roles
                  </p>
                </div>
              </div>

              {/* Detailed Comparison */}
              <div className="space-y-2">
                {comparison?.comparison?.map((item) => (
                  <div
                    key={item?.feature}
                    className={`flex items-center p-3 rounded-md ${
                      item?.isDifferent ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{item?.featureName}</p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAccessLevelColor(item?.role1Level)}`}>
                        {item?.role1Level}
                      </span>
                      <div className="flex-shrink-0">
                        {getDifferenceIcon(item?.isDifferent, item?.role1HasMore, item?.role2HasMore)}
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAccessLevelColor(item?.role2Level)}`}>
                        {item?.role2Level}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}